/**
 * usePlatformContact — Wave 42.106
 *
 * Single source of truth for the public support phone / email / hours shown
 * on the landing pages, sign-up footers, pricing CTAs, patient overview,
 * demo request page, etc. Values are stored in the `app_settings` DB table
 * and edited by Clinic Admin under App Settings → Support Contact.
 *
 * Return shape:
 *   {
 *     phone:      "+1 (937) 404-5340"          // display form
 *     phoneHref:  "tel:+19374045340"           // ready for <a href>
 *     email:      "support@myhometowndirect.com"
 *     emailHref:  "mailto:support@myhometowndirect.com"
 *     hours:      "Mon–Fri 8am–6pm · Sat 9am–3pm ET"
 *     loading, source
 *   }
 *
 * When the backend is unreachable, hardcoded fallbacks are returned so the
 * page never renders an empty phone number.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const FALLBACK = {
  phone: '+1 (937) 404-5340',
  email: 'support@myhometowndirect.com',
  hours: 'Mon–Fri 8am–6pm ET',
};

const toTelHref = (phone) => {
  const digits = String(phone || '').replace(/[^+\d]/g, '');
  if (!digits) return 'tel:';
  return digits.startsWith('+') ? `tel:${digits}` : `tel:+${digits}`;
};

// Module-level cache so we hit the endpoint once per browser session.
let _cache = null;
let _pending = null;

async function fetchContact() {
  if (_cache) return _cache;
  if (_pending) return _pending;
  _pending = axios
    .get(`${API}/api/app-settings/public/contact`, { timeout: 6000 })
    .then((res) => {
      const d = res.data || {};
      _cache = {
        phone: d.support_phone || FALLBACK.phone,
        email: d.support_email || FALLBACK.email,
        hours: d.support_hours || FALLBACK.hours,
      };
      return _cache;
    })
    .catch(() => ({ ...FALLBACK }))
    .finally(() => {
      _pending = null;
    });
  return _pending;
}

// Optional invalidator so the Clinic Admin save can refresh open tabs.
export function invalidatePlatformContactCache() {
  _cache = null;
}

export function usePlatformContact() {
  const [state, setState] = useState({ ...FALLBACK, loading: true, source: 'fallback' });
  useEffect(() => {
    let alive = true;
    fetchContact().then((d) => {
      if (!alive) return;
      setState({
        ...d,
        loading: false,
        source: _cache ? 'backend' : 'fallback',
      });
    });
    return () => {
      alive = false;
    };
  }, []);
  return {
    ...state,
    phoneHref: toTelHref(state.phone),
    emailHref: `mailto:${state.email}`,
  };
}

export default usePlatformContact;
